import { Button } from "@babackapp/customer-ui/components/button";
import {
  Option,
  OptionAction,
  OptionDescription,
  OptionsList,
  OptionTitle,
} from "@babackapp/customer-ui/components/option";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckIcon, PackageIcon, StoreIcon, UndoIcon } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { StepHeader } from "../components/step-header";
import { useSetReturnMode } from "../hooks/use-set-return-mode";
import { useWidgetParams } from "../hooks/use-widget-params";
import type { ReturnMode } from "../types";

const METHOD_CONFIG: Array<{
  mode: ReturnMode;
  label: string;
  description: string;
  icon: React.ReactNode;
}> = [
  {
    mode: "carrier",
    label: "Transporteur",
    description: "Un transporteur vient récupérer le colis chez vous",
    icon: <PackageIcon />,
  },
  {
    mode: "store",
    label: "En magasin",
    description: "Déposez votre colis dans un de nos points de dépôt",
    icon: <StoreIcon />,
  },
  {
    mode: "retractation",
    label: "Rétractation",
    description: "Retour dans le cadre du droit de rétractation (14 jours)",
    icon: <UndoIcon />,
  },
];

const AVAILABLE_RETURN_MODES: ReturnMode[] = ["carrier", "store", "retractation"];

const returnMethodSchema = z.object({
  mode: z.enum(["carrier", "store", "retractation"] as const),
});

type ReturnMethodFormValues = z.infer<typeof returnMethodSchema>;

export function ReturnMethodPage() {
  const { orderId } = useWidgetParams();
  const navigate = useNavigate();
  const setReturnMode = useSetReturnMode(orderId);

  const form = useForm<ReturnMethodFormValues>({
    resolver: zodResolver(returnMethodSchema),
  });

  const modes = METHOD_CONFIG.filter((m) => AVAILABLE_RETURN_MODES.includes(m.mode));
  const selected = form.watch("mode");

  const onSubmit = form.handleSubmit((values) => {
    setReturnMode(values.mode);
    navigate(values.mode === "store" ? "../store-selection" : "../address");
  });

  return (
    <WidgetPage>
      <StepHeader
        title="Méthode de retour"
        description="Comment souhaitez-vous retourner votre article ?"
      />
      <form onSubmit={onSubmit} className="flex flex-1 flex-col gap-4">
        <div className="flex flex-1 flex-col overflow-y-auto">
          <Controller
            control={form.control}
            name="mode"
            render={({ field }) => (
              <OptionsList>
                {modes.map(({ mode, label, description, icon }) => (
                  <Option
                    key={mode}
                    data-state={field.value === mode ? "checked" : "unchecked"}
                    onClick={() => field.onChange(mode)}
                  >
                    <OptionTitle>
                      {icon}
                      {label}
                    </OptionTitle>
                    <OptionDescription>{description}</OptionDescription>
                    <OptionAction>
                      {field.value === mode && <CheckIcon className="text-primary" />}
                    </OptionAction>
                  </Option>
                ))}
              </OptionsList>
            )}
          />
        </div>
        <div className="mt-auto flex gap-2 pb-4 shrink-0">
          <Button type="button" variant="outline" size="lg" onClick={() => navigate(-1)}>
            Retour
          </Button>
          <Button type="submit" size="lg" className="flex-1" disabled={!selected}>
            Continuer
          </Button>
        </div>
      </form>
    </WidgetPage>
  );
}
